import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {WeakSet<HTMLVideoElement>} */
  #boundVideos = new WeakSet();

  /** @type {IntersectionObserver | null} */
  #autoplayObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });

    if (this.hasAttribute('autoplay')) {
      this.loadContent(false);
      this.#setupAutoplayGuardrails();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#autoplayObserver?.disconnect();
    this.#abortController.abort();
  }

  #setupAutoplayGuardrails() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.setAttribute('data-user-paused', '');
      this.removeAttribute('autoplay');
      this.#getVideos().forEach((video) => {
        video.removeAttribute('autoplay');
      });
      this.pauseMedia();
      return;
    }

    this.#autoplayObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.playMedia();
          } else {
            this.pauseMedia();
          }
        });
      },
      { threshold: 0.1 }
    );
    this.#autoplayObserver.observe(this);
  }

  updatedCallback() {
    super.updatedCallback();

    this.#getVideos().forEach((video) => this.#bindVideoEvents(video));

    if (this.#getVideos().length) {
      this.isPlaying = this.#getVideos().some((video) => !video.paused);
    } else if (this.#getIframes().length && this.getAttribute('data-media-loaded')) {
      this.isPlaying = !this.hasAttribute('paused');
    } else {
      this.isPlaying = false;
    }

    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Unloads loaded media so the next product can load fresh content from its template.
   */
  resetMedia() {
    this.pauseMedia();
    this.#getDirectMediaElements().forEach((element) => element.remove());
    this.removeAttribute('data-media-loaded');
    this.removeAttribute('data-user-paused');
    this.removeAttribute('paused');
    this.isPlaying = false;
    this.refs.deferredMediaPlayButton?.classList.remove('deferred-media__playing');
    this.updatePlayPauseHint(false);
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.removeAttribute('data-user-paused');
    this.loadContent(true);
    this.playMedia();
  };

  /**
   * @returns {HTMLElement[]}
   */
  #getDirectMediaElements() {
    return Array.from(this.children).filter((child) => child.matches('iframe[data-video-type], video'));
  }

  /**
   * @param {HTMLVideoElement} video
   */
  #bindVideoEvents(video) {
    if (this.#boundVideos.has(video)) return;

    this.#boundVideos.add(video);
    const signal = this.#abortController.signal;

    video.addEventListener(
      'play',
      () => {
        if (this.hasAttribute('data-user-paused')) {
          video.pause();
        }
      },
      { signal }
    );
  }

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    const existingMedia = this.#getDirectMediaElements();
    if (existingMedia.length) {
      this.setAttribute('data-media-loaded', 'true');
      this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
      existingMedia.forEach((media) => {
        if (media instanceof HTMLVideoElement) this.#bindVideoEvents(media);
      });
      return;
    }

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement) {
      this.#bindVideoEvents(content);

      if (content.getAttribute('autoplay')) {
        content.play()?.catch?.(() => {});
      }
    } else if (content instanceof HTMLIFrameElement) {
      this.setAttribute('paused', '');
      this.isPlaying = false;
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.#isMediaPlaying()) {
      this.setAttribute('data-user-paused', '');
      this.pauseMedia();
      return;
    }

    this.removeAttribute('data-user-paused');
    this.playMedia();
  }

  #isMediaPlaying() {
    const videos = this.#getVideos();
    if (videos.length) return videos.some((video) => !video.paused);

    if (this.#getIframes().length && this.getAttribute('data-media-loaded')) {
      return !this.hasAttribute('paused');
    }

    return this.isPlaying;
  }

  #getVideos() {
    return Array.from(this.querySelectorAll('video'));
  }

  #getIframes() {
    return Array.from(this.querySelectorAll('iframe[data-video-type]'));
  }

  /**
   * @param {HTMLIFrameElement} iframe
   * @param {'playVideo' | 'pauseVideo'} command
   */
  #postMessageToIframe(iframe, command) {
    const message =
      iframe.dataset.videoType === 'youtube'
        ? JSON.stringify({ event: 'command', func: command, args: '' })
        : JSON.stringify({ method: command === 'playVideo' ? 'play' : 'pause' });

    iframe.contentWindow?.postMessage(message, '*');
  }

  playMedia() {
    if (this.hasAttribute('data-user-paused')) return;

    const iframes = this.#getIframes();
    if (iframes.length) {
      iframes.forEach((iframe) => this.#postMessageToIframe(iframe, 'playVideo'));
      this.isPlaying = true;
    } else {
      const videos = this.#getVideos();
      videos.forEach((video) => {
        video.play()?.catch?.(() => {
          this.isPlaying = this.#isMediaPlaying();
          if (!this.isPlaying) {
            this.setAttribute('paused', '');
            this.updatePlayPauseHint(false);
          }
        });
      });
      if (videos.length) this.isPlaying = true;
    }
    this.removeAttribute('paused');
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    const iframes = this.#getIframes();

    if (iframes.length) {
      iframes.forEach((iframe) => this.#postMessageToIframe(iframe, 'pauseVideo'));
      this.isPlaying = false;
    } else {
      this.#getVideos().forEach((video) => {
        video.pause();
      });
      this.isPlaying = this.#isMediaPlaying();
    }

    if (!this.isPlaying) {
      this.setAttribute('paused', '');
    }

    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        if (totalDistance < 10) {
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
